import logger from './logger';

/**
 * 获取扩展程序版本号
 * @returns {string} 版本号
 */
export const getExtensionVersion = () => {
  // 优先从全局变量获取（App.js中已经设置）
  if (window.CURRENT_EXTENSION_VERSION) {
    return window.CURRENT_EXTENSION_VERSION;
  }
  
  let version = null;
  
  // 方法1: 从window.EXTENSION_VERSION获取（构建时注入）
  if (window.EXTENSION_VERSION) {
    version = window.EXTENSION_VERSION;
    logger.log('[extensionUtils] 从window.EXTENSION_VERSION获取版本号:', version);
  }
  
  // 方法2: 从meta标签获取
  if (!version) {
    const metaElement = document.querySelector('meta[name="extension-version"]');
    if (metaElement && metaElement.getAttribute('content')) {
      version = metaElement.getAttribute('content');
      logger.log('[extensionUtils] 从meta标签获取版本号:', version);
    }
  }
  
  // 方法3: 从chrome.runtime.getManifest()获取（仅在扩展环境中可用）
  if (!version && typeof window.chrome !== 'undefined' && window.chrome.runtime && window.chrome.runtime.getManifest) {
    try {
      const manifest = window.chrome.runtime.getManifest();
      if (manifest && manifest.version) {
        version = manifest.version;
        logger.log('[extensionUtils] 从chrome.runtime.getManifest()获取版本号:', version);
      }
    } catch (error) {
      logger.warn('[extensionUtils] 无法从chrome.runtime.getManifest()获取版本号:', error);
    }
  }
  
  // 如果都没有获取到，使用默认版本号
  if (!version) {
    version = '0.1.0';
    logger.warn('[extensionUtils] 无法获取版本号，使用默认版本号:', version);
  }
  
  return version;
};

/**
 * 检查是否在扩展程序环境中运行
 * @returns {boolean}
 */
export const isExtensionEnvironment = () => {
  return window.location.protocol.includes('-extension:') || 
         (typeof window.chrome !== 'undefined' && window.chrome.runtime && window.chrome.runtime.id);
};

/**
 * 获取扩展程序信息
 * @returns {object} 扩展程序信息
 */
export const getExtensionInfo = () => {
  const version = getExtensionVersion();
  const isExtension = isExtensionEnvironment();
  
  return {
    version,
    isExtension,
    environment: isExtension ? 'extension' : 'web',
    userAgent: navigator.userAgent,
    platform: navigator.platform
  };
};

/**
 * 比较版本号
 * @param {string} version1 版本号1
 * @param {string} version2 版本号2
 * @returns {number} 1: version1 > version2, -1: version1 < version2, 0: 相等
 */
export const compareVersions = (version1, version2) => {
  const v1Parts = version1.split('.').map(Number);
  const v2Parts = version2.split('.').map(Number);
  
  const maxLength = Math.max(v1Parts.length, v2Parts.length);
  
  for (let i = 0; i < maxLength; i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;
    
    if (v1Part > v2Part) return 1;
    if (v1Part < v2Part) return -1;
  }
  
  return 0;
};

/**
 * 检查是否需要更新
 * @param {string} currentVersion 当前版本
 * @param {string} latestVersion 最新版本
 * @returns {boolean} 是否需要更新
 */
export const needsUpdate = (currentVersion, latestVersion) => {
  return compareVersions(currentVersion, latestVersion) < 0;
};

/**
 * 将版本号转换为数字格式（去掉点号）
 * @param {string} version 版本号字符串，如 "0.1.0"
 * @returns {number} 数字格式的版本号，如 10
 */
export const versionToNumber = (version) => {
  if (!isExtensionEnvironment() || !version || typeof version !== 'string') {
    return 0;
  }
  return parseInt(version.replace(/\./g, ''), 10);
};

/**
 * 将数字格式的版本号转换回字符串格式
 * @param {number} versionNumber 数字格式的版本号，如 10
 * @returns {string} 版本号字符串，如 "0.1.0"
 */
export const numberToVersion = (versionNumber) => {
  if (!versionNumber || typeof versionNumber !== 'number') {
    return '0.0.0';
  }
  
  const versionStr = versionNumber.toString();
  
  // 处理不同长度的版本号
  if (versionStr.length === 1) {
    return `0.0.${versionStr}`;
  } else if (versionStr.length === 2) {
    return `0.${versionStr[0]}.${versionStr[1]}`;
  } else if (versionStr.length === 3) {
    return `${versionStr[0]}.${versionStr[1]}.${versionStr[2]}`;
  } else {
    // 对于更长的版本号，取前三位
    return `${versionStr[0]}.${versionStr[1]}.${versionStr[2]}`;
  }
}; 